import { useEffect, useMemo, useState } from 'react'
import { BehaviorSubject, combineLatest } from 'rxjs'
import { getResourceObservable } from '../storage'
import { Entity, ResourceType } from '../types'

/**
 * A custom React hook that provides a synchronized list of resources.
 *
 * This hook creates a list of observables based on the provided `ids`, which are then combined
 * into a single observable using `combineLatest`. It maintains the latest values of the resources
 * as a BehaviorSubject. The current values of this BehaviorSubject are then used to set the state of
 * the resources, which is returned by the hook.
 *
 * The hook automatically subscribes to the observable list when the component mounts, and unsubscribes
 * when the component unmounts, to prevent memory leaks.
 *
 * @param _type - The type of the resources. This is used for type inference and does not affect
 *                the behavior of the hook.
 * @param ids - An array of string IDs that identify the resources to be synchronized.
 *
 * @returns An array of resources of type `Entity<T>`.
 */
export function useSyncedResourceList<T extends ResourceType>(_type: T, ids: string[]) {
  const listObservable = useMemo(() => {
    const observables = ids.map((id) => getResourceObservable<T>(id)!)
    const combo = combineLatest(observables)
    const latestValues = new BehaviorSubject<Entity<T>>([])
    combo.subscribe(latestValues)
    return latestValues
  }, [ids])

  const [resources, setResources] = useState<Array<Entity<T>>>(listObservable.getValue())

  useEffect(() => {
    const subscription = listObservable.subscribe((data) => {
      setResources(data)
    })

    return () => {
      subscription?.unsubscribe()
    }
  }, [listObservable])

  return resources
}
